import { ActionContext } from "vuex";
import { RootState } from "@/stores";
import ClientService from "@/services/client";
import KYCService from "@/services/kyc";

import { UserListItem } from "@/types";

import { ClientState } from "./index";
import {
  ClientListItem,
  CreateBankSecretRequest,
  GetClientListRequest,
  GetTransactionsRequest,
  TransactionFile,
  UpdateCompanyInfoRequest,
  UpdateBusinessOwnerRequest,
  SubmitFinancePICRequest,
  SubmitDocsRequest,
} from "@/types";
import { KYCStatusMap } from "@/proto/identity/identity/all_pb";
import { KYCDocumentReview } from "@/proto/admin/all_pb";

export async function getClientList(
  { commit, rootState }: ActionContext<ClientState, RootState>,
  search?: GetClientListRequest
): Promise<ClientListItem[]> {
  const {
    auth,
    config: { serviceUrl },
  } = rootState;

  const result = await new ClientService(
    serviceUrl,
    auth.auth?.id_token as string
  ).getClients(search);

  commit("setClientList", result);

  return result.clientList;
}

export async function getClientInfo(
  { commit, rootState }: ActionContext<ClientState, RootState>,
  clientId: string
): Promise<void> {
  const {
    auth,
    config: { serviceUrl },
  } = rootState;

  const result = await new ClientService(
    serviceUrl,
    auth.auth?.id_token as string
  ).getClientInfo(clientId);

  commit("setCurrentClient", result);
}

export async function getClientTransactions(
  { commit, rootState }: ActionContext<ClientState, RootState>,
  request: GetTransactionsRequest
): Promise<void> {
  const {
    auth,
    config: { serviceUrl },
  } = rootState;

  const result = await new ClientService(
    serviceUrl,
    auth.auth?.id_token as string
  ).getClientTransactions(request);

  commit("setTransactions", result);
}

export async function downloadClientTransactions(
  { rootState }: ActionContext<ClientState, RootState>,
  request: GetTransactionsRequest
): Promise<TransactionFile | null> {
  const {
    auth,
    config: { serviceUrl },
  } = rootState;

  const result = await new ClientService(
    serviceUrl,
    auth.auth?.id_token as string
  ).exportClientTransactions(request);

  return result;
}

export async function getClientIntegrations(
  { commit, rootState }: ActionContext<ClientState, RootState>,
  clientId: string
): Promise<void> {
  const {
    auth,
    config: { serviceUrl },
  } = rootState;

  const result = await new ClientService(
    serviceUrl,
    auth.auth?.id_token as string
  ).getClientIntegrations(clientId);

  commit("setIntegrations", result);
}

export async function getClientIntegrationInfo(
  { commit, rootState }: ActionContext<ClientState, RootState>,
  id: string
): Promise<any> {
  const {
    auth,
    config: { serviceUrl },
  } = rootState;

  const result = await new ClientService(
    serviceUrl,
    auth.auth?.id_token as string
  ).getClientIntegrationInfo(id);

  commit("setCurrentIntegration", result);
}

export async function upsertIntegration(
  { rootState }: ActionContext<ClientState, RootState>,
  payload: CreateBankSecretRequest
): Promise<void> {
  const {
    auth,
    config: { serviceUrl },
  } = rootState;

  await new ClientService(
    serviceUrl,
    auth.auth?.id_token as string
  ).upsertClientIntegration(payload);
}

export async function deleteIntegration(
  { rootState }: ActionContext<ClientState, RootState>,
  id: string
): Promise<void> {
  const {
    auth,
    config: { serviceUrl },
  } = rootState;

  await new ClientService(
    serviceUrl,
    auth.auth?.id_token as string
  ).deleteClientIntegration(id);
}

export async function checkLiveAccessPermission({
  rootState,
}: ActionContext<ClientState, RootState>): Promise<boolean> {
  const {
    auth,
    config: { serviceUrl },
  } = rootState;

  try {
    const data = await new ClientService(
      serviceUrl,
      auth.auth?.id_token as string
    ).checkLiveAccessPermission();

    return data;
  } catch (error) {
    return false;
  }
}

export async function updateLiveAccess(
  { rootState }: ActionContext<ClientState, RootState>,
  {
    clientId,
    isLive,
    reason,
  }: { clientId: string; isLive: boolean; reason: string | null }
): Promise<void> {
  const {
    auth,
    config: { serviceUrl },
  } = rootState;

  await new ClientService(
    serviceUrl,
    auth.auth?.id_token as string
  ).updateOrgLiveAccess(clientId, isLive, reason);
}

export async function updateCompanyInfo(
  { rootState }: ActionContext<ClientState, RootState>,
  { clientId, data }: UpdateCompanyInfoRequest
): Promise<void> {
  const {
    auth,
    config: { serviceUrl },
  } = rootState;

  await new ClientService(
    serviceUrl,
    auth.auth?.id_token as string
  ).updateCompanyInformation(clientId, data);
}

export async function submitDocuments(
  { rootState }: ActionContext<ClientState, RootState>,
  { clientId, userType, country, domicile, payload }: SubmitDocsRequest
): Promise<void> {
  const {
    auth,
    config: { serviceUrl },
    client: { currentClient },
  } = rootState;

  await new ClientService(
    serviceUrl,
    auth.auth?.id_token as string
  ).submitDocuments(
    clientId,
    userType,
    country,
    domicile,
    payload,
    currentClient?.supportingDocsData?.previousDocumentIds || []
  );
}

export async function updateBusinessOwner(
  { rootState }: ActionContext<ClientState, RootState>,
  { clientId, data }: UpdateBusinessOwnerRequest
): Promise<void> {
  const {
    auth,
    config: { serviceUrl },
  } = rootState;

  await new ClientService(
    serviceUrl,
    auth.auth?.id_token as string
  ).updateClientBusinessOwner(clientId, data);
}

export async function updateKycStatus(
  { rootState }: ActionContext<ClientState, RootState>,
  {
    clientId,
    kycStatus,
  }: { clientId: string; kycStatus: KYCStatusMap[keyof KYCStatusMap] }
): Promise<void> {
  const {
    auth,
    config: { serviceUrl },
  } = rootState;

  await new KYCService(
    serviceUrl,
    auth.auth?.id_token as string
  ).updateKycStatus(clientId, kycStatus);
}

export async function rejectKYCApplication(
  { rootState }: ActionContext<ClientState, RootState>,
  clientId: string
): Promise<void> {
  const {
    auth,
    config: { serviceUrl },
  } = rootState;

  await new KYCService(
    serviceUrl,
    auth.auth?.id_token as string
  ).rejectKYCApplication(clientId);
}

export async function requestResubmitDocs(
  { rootState }: ActionContext<ClientState, RootState>,
  {
    clientId,
    payload,
  }: {
    clientId: string;
    payload: KYCDocumentReview.AsObject[];
  }
): Promise<void> {
  const {
    auth,
    config: { serviceUrl },
  } = rootState;

  await new ClientService(
    serviceUrl,
    auth.auth?.id_token as string
  ).requestResubmitDocs(clientId, payload);
}

export async function saveFinancePICForm(
  { rootState }: ActionContext<ClientState, RootState>,
  { clientId, domicileType, data }: SubmitFinancePICRequest
): Promise<void> {
  const {
    auth,
    config: { serviceUrl },
  } = rootState;

  await new ClientService(
    serviceUrl,
    auth.auth?.id_token as string
  ).submitFinancePIC(clientId, domicileType, data);
}

export async function removeFinancePICForm(
  { rootState }: ActionContext<ClientState, RootState>,
  { clientId, picId }: { clientId: string; picId: string }
): Promise<void> {
  const {
    auth,
    config: { serviceUrl },
  } = rootState;

  await new ClientService(
    serviceUrl,
    auth.auth?.id_token as string
  ).removeFinancePIC(clientId, picId);
}

export async function getListUsers(
  { commit, rootState }: ActionContext<ClientState, RootState>,
  { clientId }: { clientId: string; page: number }
): Promise<UserListItem[]> {
  const {
    auth,
    config: { serviceUrl },
  } = rootState;
  const result = await new ClientService(
    serviceUrl,
    auth.auth?.id_token as string
  ).getListUsers(clientId);

  commit("setUserList", result);

  return result;
}

export async function deactivateUser(
  { rootState }: ActionContext<ClientState, RootState>,
  { userId }: { userId: string }
): Promise<void> {
  const {
    auth,
    config: { serviceUrl },
  } = rootState;
  await new ClientService(
    serviceUrl,
    auth.auth?.id_token as string
  ).deactivateUser(userId);
}
